import { events, pageViewTypes } from "@constants/tracking";
/* ----------------- React --------------- */
import { useMemo, useState } from "react";

import { useCustomNavigation } from "@hooks/useCustomNavigation";
/* ----------------- Hooks --------------- */
import { useMoreSettings } from "../hooks/useMoreSettings";

import { PurchaseDialog } from "@features/dashboard/components/PurchaseDialog";
import { ExamsFilterDialog } from "components/ExamsFilterDialog";
/* ----------------- Components --------------- */

/* ----------------- UI --------------- */
import { useDevice } from "@memorang/ui/src/useDevice";
import { Portal } from "react-native-paper";

import type { ProductType } from "@memorang/types/src/bundle";
/* ----------------- Types --------------- */
import type { MoreSettingsItem } from "../types";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { defaultFeedbackSubtitle } from "@features/feedback/constants";
import { ChangeAvatarDialog } from "@features/profile/dialogs/ChangeAvatarDialog";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";

import { FeedbackDialog } from "@features/feedback/components/dialogs/FeedbackDialog";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import MoreApplet from "@memorang/ui/src/components/MoreApplet";
import type { FontSchemaType } from "@memorang/ui/src/types";
import { trackCustomEvent } from "analytics";
import SnackBarCustom from "components/CustomSnackbar";
import SetExamDateContainer from "components/SetExamDateContainer";
/* ----------------- Helpers --------------- */
import { router, usePathname } from "expo-router";
import { useTranslation } from "react-i18next";

const MoreRouteContainer = () => {
	const { isMobile } = useDevice();
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const { t } = useTranslation();
	const { newUserInfo } = useAuthContext();

	const {
		moreSettingsSections,
		showDatePicker,
		toggleShowDatePicker,
		showExamsFilterDialog,
		toggleShowExamsFilterDialog,
		purchasedProducts,
		hasMoreThanOneExam,
	} = useMoreSettings();
	const memoizedAnalyticsParams = useMemo(() => {
		return {
			type: pageViewTypes.more,
		};
	}, []);
	useAnalytics(memoizedAnalyticsParams);
	const setShowAvatarUpdatedMessage = useGlobalStore(
		(state) => state.setShowAvatarUpdatedMessage,
	);
	const pathname = usePathname();

	const showAvatarUpdatedMessage = useGlobalStore(
		(state) => state.showAvatarUpdatedMessage,
	);
	const toggleShowFeedbackDialog = () => {
		setShowFeedbackDialog((prev) => !prev);
	};
	const [selectedProductType, setSelectedProductType] = useState<
		ProductType | undefined
	>(undefined);
	const { handleFeedbackNavigation } = useCustomNavigation();

	const [showChangeAvatarDialog, setShowChangeAvatarDialog] = useState(false);
	const { handleChangeAvatarNavigation } = useCustomNavigation();

	const toggleChangeAvatarDialog = () => {
		setShowChangeAvatarDialog(!showChangeAvatarDialog);
	};

	const { handleExamsFilterNavigation } = useCustomNavigation();

	const handleNavigateToPurchaseView = (id?: string) => {
		const productItem = purchasedProducts?.find(
			(item) => item.bundle.id === id,
		);
		if (productItem) {
			if (isWeb) {
				setSelectedProductType(productItem.bundle.bundleType);
			} else {
				router.push({
					pathname: "/purchase",
					params: {
						productType: productItem.bundle.bundleType,
					},
				});
			}
		}
	};
	const appFont = useAppStore((store) => store.app.font);

	const handleClickMoreSettingsItem = (item: MoreSettingsItem) => {
		switch (item.type) {
			case "profile":
				router.push("/profile");
				break;
			case "change-date":
				toggleShowDatePicker();
				break;
			case "switch-exam":
				if (hasMoreThanOneExam) {
					handleExamsFilterNavigation(toggleShowExamsFilterDialog);
				}
				break;
			case "subscription":
				handleNavigateToPurchaseView(item.id);
				break;
			case "change-avatar":
				handleChangeAvatarNavigation(toggleChangeAvatarDialog);
				break;
			case "redeem":
				router.push("/redeem");
				break;
			default:
				break;
		}
	};

	const showPurchaseDialog = !!selectedProductType;
	return (
		<>
			<MoreApplet
				sections={moreSettingsSections}
				handleClickMoreSettingsItem={handleClickMoreSettingsItem}
				appFont={appFont as FontSchemaType}
				CustomAppBar={{
					options: {
						headerTitle: "More settings",
						subtitle: isMobile ? "" : "Explore additional settings here",
					},
					showCommandBar: isWeb,
					handleFeedbackNavigation: () => {
						handleFeedbackNavigation(toggleShowFeedbackDialog);
					},
					showUserMenu: true,
					newUserInfo: newUserInfo,
					onAvatarPress: () => {
						router.push("/(protected)/account/settings");
					},

					handleTrackEvent: () => {
						trackCustomEvent({
							eventName: events.commandBarClicked,
							option: "command_bar",
						});
					},

					showHambugerMenu: true,
				}}
			/>
			<SetExamDateContainer
				showDatePicker={showDatePicker}
				toggleShowDatePicker={toggleShowDatePicker}
			/>
			{showExamsFilterDialog && (
				<ExamsFilterDialog
					visible={showExamsFilterDialog}
					handleClose={toggleShowExamsFilterDialog}
				/>
			)}
			{showPurchaseDialog ? (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={() => setSelectedProductType(undefined)}
					productType={selectedProductType}
				/>
			) : null}
			{showChangeAvatarDialog && (
				<ChangeAvatarDialog
					visible={showChangeAvatarDialog}
					handleClose={toggleChangeAvatarDialog}
				/>
			)}
			{showAvatarUpdatedMessage && (
				<Portal>
					<SnackBarCustom
						message="Avatar updated successfully"
						duration={2000}
						visible
						handleDismiss={() => {
							setShowAvatarUpdatedMessage(false);
						}}
					/>
				</Portal>
			)}
			{isWeb && (
				<FeedbackDialog
					visible={showFeedbackDialog}
					view={pathname}
					feedbackSubtitle={t(defaultFeedbackSubtitle)}
					handleDismiss={toggleShowFeedbackDialog}
				/>
			)}
		</>
	);
};

export default MoreRouteContainer;
